import { Clipboard, Search, Sparkles, X } from "lucide-react";
import { GAME_TYPES, GENERATION_STYLES, type GameType, type GenerationStyle, type NameSuggestion } from "@handlequest/shared";
import { useState } from "react";
import { generateNames } from "../lib/api";
import { TurnstileWidget } from "./TurnstileWidget";

interface AIGeneratorProps {
  open: boolean;
  onClose: () => void;
  onCheck: (username: string) => void;
  theme?: "dark" | "light";
  turnstileSiteKey?: string;
}

export function AIGenerator({ open, onClose, onCheck, theme = "dark", turnstileSiteKey = "" }: AIGeneratorProps) {
  const [style, setStyle] = useState<GenerationStyle>("sci-fi");
  const [gameType, setGameType] = useState<GameType>("fps");
  const [keywords, setKeywords] = useState("");
  const [suggestions, setSuggestions] = useState<NameSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);

  async function runGenerate() {
    setLoading(true);
    setError(null);
    try {
      const data = await generateNames({
        style,
        gameType,
        keywords: keywords.split(",").map((keyword) => keyword.trim()).filter(Boolean),
        language: "en",
        count: 10,
        ...(turnstileSiteKey && turnstileToken ? { turnstileToken } : {})
      });
      setSuggestions(data.suggestions);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Name generation failed.");
    } finally {
      setLoading(false);
      if (turnstileSiteKey) {
        setTurnstileToken(null);
        setTurnstileResetSignal((current) => current + 1);
      }
    }
  }

  if (!open) {
    return null;
  }

  return (
    <>
      <div className="panel-backdrop open" onClick={onClose} />
      <aside className="ai-panel open" aria-labelledby="ai-generator-title">
        <header className="panel-header">
          <div>
            <p className="eyebrow">AI names</p>
            <h2 id="ai-generator-title">Name forge</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close generator">
            <X size={19} aria-hidden="true" />
          </button>
        </header>

        <div className="control-group">
          <span className="control-label">Style</span>
          <div className="segmented">
            {GENERATION_STYLES.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={style === item.id}
                onClick={() => setStyle(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="control-group">
          <span className="control-label">Game type</span>
          <div className="segmented">
            {GAME_TYPES.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={gameType === item.id}
                onClick={() => setGameType(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <label className="field-label">
          Keywords
          <input
            className="text-input"
            value={keywords}
            onChange={(event) => setKeywords(event.target.value)}
            placeholder="nova, phantom"
          />
        </label>

        <TurnstileWidget
          onToken={setTurnstileToken}
          resetSignal={turnstileResetSignal}
          siteKey={turnstileSiteKey}
          theme={theme}
        />

        <button className="ai-button full" type="button" onClick={runGenerate} disabled={loading || Boolean(turnstileSiteKey && !turnstileToken)}>
          <Sparkles size={18} aria-hidden="true" />{loading ? "Generating" : "Generate 10 names"}
        </button>
        {error && <p className="error-line">{error}</p>}

        <div className="generated-list" aria-live="polite">
          {suggestions.map((suggestion) => (
            <article className="name-row" key={suggestion.username}>
              <strong>{suggestion.username}</strong>
              <span>{suggestion.score}</span>
              <button className="micro-button" type="button" onClick={() => navigator.clipboard.writeText(suggestion.username)} aria-label={`Copy ${suggestion.username}`}>
                <Clipboard size={15} aria-hidden="true" />
              </button>
              <button className="micro-button" type="button" onClick={() => onCheck(suggestion.username)} aria-label={`Check ${suggestion.username}`}>
                <Search size={15} aria-hidden="true" />
              </button>
            </article>
          ))}
        </div>
      </aside>
    </>
  );
}
